import React, { useEffect, useMemo } from "react";
import { Router } from "wouter";
import { navigate, useLocationProperty } from "wouter/use-browser-location";
import rison from "rison";
import Routes from "./routes";
import PageWrapper from "../app/ui/framework/pageWrapper";
import BackboneWrapper from "../app/ui/framework/backboneWrapper";
import TopBarViewModules from "../app/modules/core/navigation/topbar/main";
import * as logger from "../app/utils/logger";
import constants from "../app/constants";
import { FeatureFlagName } from "../app/utils/featureFlagName";
import { useStoreSelector } from "../app/ui/framework/hooks/useStoreSelector";
import AppMenu from "../app/modules/core/navigation/menu/main";

const hashLocation = () => {
    const pageMatch = /^#\/(.*?)(?:,|$)/.exec(window.location.hash);
    return pageMatch && pageMatch.length > 1 ? `/${ pageMatch[1] }` : "/";
};

const hashNavigate = (to: string, state?: any) =>
    navigate(`#${ to }`, state && Object.keys(state).length > 0 ? { replace: true, state } : undefined);

const useHashLocation = (): [string, (to: string) => void] => {
    const location = useLocationProperty(hashLocation);

    // Handle rison parameters on any navigation (including after router is mounted)
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            const pageWithStateMatch = /^#\/(.*?)(?:,(.*))$/.exec(hash);

            if (pageWithStateMatch && pageWithStateMatch?.length > 2) {
                const path = pageWithStateMatch[1];

                try {
                    const decodedState = rison.decode<any>(pageWithStateMatch[2]);
                    // Silent redirect: replace the URL without rison params and set history state
                    hashNavigate(`/${ path }`, decodedState);
                } catch (e) {
                    logger.error(logger.Source.ERROR, `ReactRouter: Failed to decode rison state from URL: ${ e?.message || "Unknown error" }`);
                    // Still redirect but without state
                    hashNavigate(`/${ path }`);
                }
            }
        };

        // Handle current hash on mount or location change
        handleHashChange();

        // Listen for hash changes (for direct URL changes or programmatic navigation)
        window.addEventListener("hashchange", handleHashChange);

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [location]); // Re-run when location changes

    return [location, hashNavigate];
};

const App: React.FC = () => {
    const nightMode = useStoreSelector(window.driveApp.store, (state) => state.data.nightMode);
    const isEmbeddedMode = useStoreSelector(window.driveApp.store, (state) => state.mobile.embed);

    // Optimize selector to only extract needed properties instead of entire pageState object
    const isMenuHidden = useStoreSelector(window.driveApp.store, state => state.mobile.pageState.isLocked || state.mobile.pageState?.pageLevel !== 1);

    // Memoize elementClasses arrays to prevent unnecessary re-renders
    const topbarClasses = useMemo(
        () => ["topbar", isEmbeddedMode ? "topbar_embed" : ""].filter(Boolean),
        [isEmbeddedMode]
    );

    const menuClasses = useMemo(
        () => ["footer-menu", isMenuHidden ? "hidden" : ""].filter(Boolean),
        [isMenuHidden]
    );

    const hidePageBasedOnFeatureFlags = () => {
        // Don't hide when lockscreen is on
        const pageState = window.driveApp?.store.getState().mobile.pageState;
        if (pageState?.isLocked) {
            return;
        }

        const currentHash = window.location.hash;
        const hasCoachingFlag = window.driveApp?.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_COACHING_MODULE);
        const hasColdChainFlag = window.driveApp?.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_COLD_CHAIN);
        const isColdChainPage = currentHash.indexOf(constants.COLD_CHAIN_ROUTE) > -1 && hasColdChainFlag;
        const isReactPage = currentHash.indexOf("driverSafety") > -1 || currentHash.indexOf("inspections") > -1
            || (hasCoachingFlag && currentHash.indexOf("coaching") > -1) || isColdChainPage;

        if (isReactPage) {
            document.getElementById("main")?.classList.add("hidden");
            document.getElementById("root")?.classList.remove("hidden");
        } else {
            document.getElementById("main")?.classList.remove("hidden");
            document.getElementById("root")?.classList.add("hidden");
        }
    };

    // Set initial visibility state for react-content based on current page
    useEffect(() => {
        hidePageBasedOnFeatureFlags();
    }, [nightMode]);

    hidePageBasedOnFeatureFlags();


    return (
        <Router base="/-" hook={useHashLocation}>
            <PageWrapper app={window.driveApp}>
                <BackboneWrapper
                    reactComponent={TopBarViewModules}
                    backboneView={window.driveApp.topbarView}
                    elementId="topbar"
                    containerId="topbar-container"
                    isZenithEnabled={false}
                    elementClasses={topbarClasses}
                />
                <div id="react-content">
                    <Routes/>
                </div>
                <BackboneWrapper
                    reactComponent={AppMenu}
                    backboneView={window.driveApp.menuView}
                    elementId="footer-menu"
                    containerId="footer-menu-container"
                    isZenithEnabled={false}
                    elementClasses={menuClasses}
                />
            </PageWrapper>
        </Router>
    );
};
export default App;
